import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';

import { RateType } from '__generated__/globalTypes';
import { PriceYieldPoint, rateAsBps, rateAsPercent } from './utils';

// The keys double as the spreadsheet's header row. Blank cells are '' (not
// null): a curve that doesn't cover a price still gets its column header.
export type PriceYieldCurveRow = {
  'Price (% of UPB)': number;
  'Yield to Maturity (%)': number | '';
  'Yield Duration (yrs)': number | '';
  'Spread (bps)': number | '';
  'Spread Duration (yrs)': number | '';
};

export type AssumptionRow = {
  Assumption: string;
  Value: string | number;
};

// 6 decimals covers the backend's emitted precision (rate is 8dp as a
// fraction = 6dp as a percent) while trimming float-division noise from the
// price-percent conversion.
const EXPORT_DECIMALS = 6;

const roundForExport = (value: number): number => {
  const factor = 10 ** EXPORT_DECIMALS;
  return Math.round(value * factor) / factor;
};

// A point with any non-finite field (NaN/Infinity from a malformed backend
// row) is junk: pricePercent can't be compared with >= to drive the merge
// below, and rate/durationYears would round to a literal NaN in an exported
// cell. Such a point contributes nothing rather than emitting a partial row.
const finitePoints = (points: PriceYieldPoint[]): PriceYieldPoint[] =>
  points.filter(
    (point) =>
      Number.isFinite(point.pricePercent) &&
      Number.isFinite(point.rate) &&
      Number.isFinite(point.durationYears),
  );

// Merge the yield and spread curves into one price-descending table. The two
// curves share a price grid where they overlap (ENG-2749) but keep their own
// tails — e.g. the spread curve is suppressed above par — so this is a
// two-pointer merge over descending prices: equal prices share a row, a price
// only one curve covers leaves the other curve's cells blank.
export const buildPriceYieldCurveRows = (
  rawYieldPoints: PriceYieldPoint[],
  rawSpreadPoints: PriceYieldPoint[],
): PriceYieldCurveRow[] => {
  const yieldPoints = finitePoints(rawYieldPoints);
  const spreadPoints = finitePoints(rawSpreadPoints);
  const rows: PriceYieldCurveRow[] = [];
  let yieldIndex = 0;
  let spreadIndex = 0;

  while (yieldIndex < yieldPoints.length || spreadIndex < spreadPoints.length) {
    const yieldPoint = yieldPoints[yieldIndex];
    const spreadPoint = spreadPoints[spreadIndex];

    const takeYield =
      yieldPoint != null &&
      (spreadPoint == null || yieldPoint.pricePercent >= spreadPoint.pricePercent);
    const takeSpread =
      spreadPoint != null &&
      (yieldPoint == null || spreadPoint.pricePercent >= yieldPoint.pricePercent);

    rows.push({
      'Price (% of UPB)': roundForExport(
        takeYield ? yieldPoint.pricePercent : spreadPoint.pricePercent,
      ),
      'Yield to Maturity (%)': takeYield ? roundForExport(rateAsPercent(yieldPoint.rate)) : '',
      'Yield Duration (yrs)': takeYield ? roundForExport(yieldPoint.durationYears) : '',
      'Spread (bps)': takeSpread ? roundForExport(rateAsBps(spreadPoint.rate)) : '',
      'Spread Duration (yrs)': takeSpread ? roundForExport(spreadPoint.durationYears) : '',
    });

    if (takeYield) yieldIndex += 1;
    if (takeSpread) spreadIndex += 1;
  }

  return rows;
};

const RATE_TYPE_LABELS: Record<RateType, string> = {
  [RateType.Sofr]: 'SOFR',
  [RateType.RiskFree]: 'Risk Free',
};

// The scenario assumptions the curve was computed under, as their own sheet.
// Exports from different scenarios are otherwise indistinguishable — the
// whole workflow is running many CPR x CDR combinations and comparing files.
export const buildAssumptionRows = (formValues: CashFlowsValidValues): AssumptionRow[] => {
  const rows: AssumptionRow[] = [
    { Assumption: `Prepayment ${formValues.prepaymentAssumptionType} (%)`, Value: formValues.cpr },
    { Assumption: `Default ${formValues.defaultAssumptionType} (%)`, Value: formValues.cdr },
    { Assumption: 'Loss Severity (%)', Value: formValues.lsr },
    { Assumption: 'Days Delay', Value: formValues.daysDelay },
  ];
  if (formValues.servicingRate != null) {
    rows.push({ Assumption: 'Servicing Rate (%)', Value: formValues.servicingRate });
  }
  if (formValues.rateType != null) {
    rows.push({ Assumption: 'Rate Type', Value: RATE_TYPE_LABELS[formValues.rateType] });
  }
  return rows;
};

export const priceYieldCurveFileName = (formValues: CashFlowsValidValues): string => {
  const scenario = [
    `${formValues.prepaymentAssumptionType}${formValues.cpr}`,
    `${formValues.defaultAssumptionType}${formValues.cdr}`,
    `LSR${formValues.lsr}`,
  ].join('_');
  return `PriceYieldCurve_${scenario}.xlsx`;
};
